import classNames from 'classnames'
import { observer } from 'mobx-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import style from './SelectModelCompare.module.less'
// import { ServerDataTypes } from '@/typings/api'
import { userService } from '@/services/userService'
import { Checkbox } from 'antd'

export interface SelectTranslateModel extends ServerDataTypes.Model {
  isSelected: boolean
}

type SelectModelCompare = {
  className?: string
  excludeModelList: string[]
  onHandleModelChange: (model: SelectTranslateModel) => void
}

const SelectModelCompare = observer((props: SelectModelCompare) => {
  const { className, excludeModelList, onHandleModelChange } = props
  const [modelList, setModelList] = useState<SelectTranslateModel[]>([])
  const [freeModelList, setFreeModelList] = useState<SelectTranslateModel[]>([])
  const [premiumModelList, setPremiumModelList] = useState<SelectTranslateModel[]>([])
  const [ultraModelList, setUltraModelList] = useState<SelectTranslateModel[]>([])

  const isUltra = userService.vipVersion === 'ultra'
  const isFree = userService.vipVersion === 'free'

  // 稳定excludeModelList引用，避免不必要的重新渲染
  const excludeModelListString = excludeModelList.join(',')
  const stableExcludeModelList = useMemo(() => excludeModelList, [excludeModelListString])

  const onUpgradeClick = (level: 'max' | 'ultra') => {
    userService.doPay({
      from: 'selectModelCompare',
      version: level,
      feature: 'other',
    })
  }

  useEffect(() => {
    const fetchModelList = async () => {
      const dataList = await userService.getLLMModelList('translate')

      const newList: SelectTranslateModel[] = dataList.map((item) => {
        const isExclude = stableExcludeModelList.find((modelKey) => modelKey === item.key)

        // 非Ultra会员，不选择顶级模型
        if (item.level === 3 && !isUltra) {
          return {
            ...item,
            isSelected: false,
          }
        }

        return {
          ...item,
          isSelected: !isExclude,
        }
      })

      const freeModelList = newList.filter((item) => item.level === 1)
      const premiumModelList = newList.filter((item) => item.level === 2)
      const ultraModelList = newList.filter((item) => item.level === 3)

      setModelList(newList)
      setFreeModelList(freeModelList)
      setPremiumModelList(premiumModelList)
      setUltraModelList(ultraModelList)
    }

    fetchModelList()
  }, [stableExcludeModelList, isUltra])

  const handleModelItemClick = useCallback(
    async (model: SelectTranslateModel) => {
      // 顶级模型仅 Ultra 会员可选
      if (model.level === 3 && !isUltra) {
        return
      }

      const selectResult = !model.isSelected
      model.isSelected = selectResult

      onHandleModelChange(model)
    },
    [isUltra, onHandleModelChange]
  )

  const renderModelItem = (model: SelectTranslateModel) => (
    <div
      key={model.key}
      className={style.modelItem}
      onClick={() => handleModelItemClick(model)}
      title={model.level === 3 && !isUltra ? '仅 Ultra 会员可选' : ''}
      style={model.level === 3 && !isUltra ? { cursor: 'not-allowed', opacity: 0.6 } : undefined}
    >
      <Checkbox
        checked={model.isSelected}
        className={style.checkBox}
        disabled={model.level === 3 && !isUltra}
      />
      <div className={style.modelIcon}>
        <img src={model.icon} alt={model.name} />
      </div>
      <div className={style.modelInfo}>
        <div className={style.modelName}>{model.name}</div>
      </div>
      {model.level === 2 && <div className={classNames(style.proBadge, style.max)}>Max</div>}
      {model.level === 3 && <div className={classNames(style.proBadge, style.ultra)}>Ultra</div>}
    </div>
  )

  return (
    <div className={classNames(style.selectModelCompare, className)}>
      {/* 基础模型（免费使用） */}
      {freeModelList.length > 0 && (
        <div className={style.modelGroup}>
          <div className={style.groupTitle}>基础模型（免费使用）</div>
          <div className={style.groupContent}>{freeModelList.map(renderModelItem)}</div>
        </div>
      )}

      {/* 高级模型 */}
      {premiumModelList.length > 0 && (
        <div className={style.modelGroup}>
          <div className={style.groupTitle}>
            高级模型
            {isFree && (
              <span
                className={classNames(style.upgrade, style.max)}
                onClick={() => onUpgradeClick('max')}
              >
                畅享高级模型
              </span>
            )}
          </div>
          <div className={style.groupContent}>{premiumModelList.map(renderModelItem)}</div>
        </div>
      )}

      {/* 顶级模型 */}
      {ultraModelList.length > 0 && (
        <div className={style.modelGroup}>
          <div className={style.groupTitle}>
            顶级模型
            {!isUltra && (
              <span
                className={classNames(style.upgrade, style.ultra)}
                onClick={() => onUpgradeClick('ultra')}
              >
                解锁顶级模型
              </span>
            )}
          </div>
          <div className={style.groupContent}>{ultraModelList.map(renderModelItem)}</div>
        </div>
      )}
    </div>
  )
})

export default SelectModelCompare
